import React, {
  useState,
  Fragment,
} from 'react';
import type {
  SvgIconComponent,
} from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import Button from 'pages/Dashboard/components/Button';
import SendConsentFormDialog from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/SendConsentFormDialog';
import usePCCPopoverScreen from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/hooks';
import PCCLearnMoreLink from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCLearnMoreLink';
import {
  PCCProgramSelectionRow,
  PCCPopoverStatusRow,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCPopoverProgramRow';
import {
  PCCProgramState,
  PCCProgramStatus,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';
import {
 PCC_LEARN_MORE_URL,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCActivationSettingsPanel';

const NON_ACTIONABLE_STATE_ORDER: Record<PCCProgramState, number> = {
  enrolled: 0,
  optedOut: 1,
  notEligible: 2,
  eligible: 3,
};

function compareNonActionablePrograms(
  left: PCCProgramStatus,
  right: PCCProgramStatus,
): number {
  return NON_ACTIONABLE_STATE_ORDER[left.state] - NON_ACTIONABLE_STATE_ORDER[right.state];
}

function getEnrollButtonLabel(
  selectedProgramTypes: PCCProgram[],
  programs: PCCProgramStatus[],
): string {
  if (selectedProgramTypes.length === 0) {
    return 'Enroll';
  }

  if (selectedProgramTypes.length === 1) {
    const selectedProgram = programs.find(
      (program) => program.type === selectedProgramTypes[0],
    );
    const programLabel = selectedProgram?.label ?? '';

    return notEmptyString(programLabel)
      ? `Enroll in ${programLabel}`
      : 'Enroll';
  }

  return `Enroll in ${selectedProgramTypes.length} programs`;
}

const PCC_STATUS_ICON: Record<PCCProgramState, {
  Icon: SvgIconComponent;
  className: string;
}> = {
  enrolled: { Icon: CheckRoundedIcon, className: 'text-teal-600' },
  eligible: { Icon: AddRoundedIcon, className: 'text-blue-500' },
  notEligible: { Icon: RemoveRoundedIcon, className: 'text-gray-500' },
  optedOut: { Icon: RemoveRoundedIcon, className: 'text-gray-500' },
};

type HeaderProps = {
  title: string;
  className?: string;
};

function Header({
  title,
  className,
}: HeaderProps) {
  return (
    <div className={clsx('flex items-center justify-between gap-2', className)}>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <PCCLearnMoreLink url={PCC_LEARN_MORE_URL} label="Learn more" />
    </div>
  );
}

type ActivatePCCTooltipContentProps = {
  isCcmApplicable: boolean;
};

export function ActivatePCCTooltipContent({
  isCcmApplicable,
}: ActivatePCCTooltipContentProps) {
  const description = isCcmApplicable
    ? 'Populate Continuous Care unifies TEMPO, CCM, and RTM into one coordinated between-visit care experience for eligible patients.'
    : 'Populate Continuous Care unifies TEMPO and RTM into one coordinated between-visit care experience for eligible patients.';

  return (
    <div className="flex w-80 flex-col gap-2">
      <Header title="PCC programs" />
      <p className="text-xs text-gray-600">
        {description}
      </p>
    </div>
  );
}

type ActivationPendingStepState = 'completed' | 'current' | 'upcoming';

type ActivationPendingStep = {
  title: string;
  description?: string;
  state: ActivationPendingStepState;
};

const ACTIVATION_PENDING_STEPS: ActivationPendingStep[] = [
  {
    title: 'Request sent',
    description: 'We have received your request.',
    state: 'completed',
  },
  {
    title: 'In review',
    description: 'Our team will follow up',
    state: 'current',
  },
  {
    title: 'Ready to enroll patients',
    description: 'All programs available',
    state: 'upcoming',
  },
];

type ActivationPendingStepMarkerProps = {
  state: ActivationPendingStepState;
};

function ActivationPendingStepMarker({
  state,
}: ActivationPendingStepMarkerProps) {
  return (
    <div
      className={clsx(
        'relative z-[1] flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        {
          'bg-emerald-500 text-white': state === 'completed',
          'border-2 border-amber-500 bg-white': state === 'current',
          'border border-gray-300 bg-white': state === 'upcoming',
        },
      )}
    >
      {state === 'completed' ? (
        <CheckRoundedIcon className="!h-3.5 !w-3.5 !text-inherit" />
      ) : null}
      {state === 'current' ? (
        <span className="h-2 w-2 rounded-full bg-amber-500" />
      ) : null}
    </div>
  );
}

type ActivationPendingStepRowProps = {
  step: ActivationPendingStep;
  isLast: boolean;
};

function ActivationPendingStepRow({
  step,
  isLast,
}: ActivationPendingStepRowProps) {
  return (
    <div className="relative flex gap-3">
      {!isLast ? (
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-2.5 top-5 w-px -translate-x-1/2 bg-gray-200"
        />
      ) : null}
      <ActivationPendingStepMarker state={step.state} />
      <div className={clsx('flex min-w-0 flex-col', { 'pb-6': !isLast })}>
        <span className="text-xs font-semibold text-gray-900">
          {step.title}
        </span>
        {notEmptyString(step.description) ? (
          <span className="text-xs text-gray-500">
            {step.description}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function PCCActivationPendingContent() {
  return (
    <div className="flex w-[360px] flex-col gap-6 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            PCC activation
          </h3>
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            Requested
          </span>
        </div>
        <PCCLearnMoreLink url={PCC_LEARN_MORE_URL} label="Learn more" />
      </div>
      <div className="flex flex-col">
        {ACTIVATION_PENDING_STEPS.map((step, index) => (
          <ActivationPendingStepRow
            key={step.title}
            step={step}
            isLast={index === ACTIVATION_PENDING_STEPS.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

type ProgramStatusMarkerProps = {
  program: PCCProgramStatus;
};

function ProgramStatusMarker({
  program,
}: ProgramStatusMarkerProps) {
  const statusIcon = PCC_STATUS_ICON[program.state];

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5',
        statusIcon.className,
      )}
    >
      <program.Icon className="!h-4 !w-4" />
      <statusIcon.Icon className="!h-3.5 !w-3.5" />
    </span>
  );
}

type ConsentRowProps = {
  onSendConsent: () => void;
};

function ConsentRow({
  onSendConsent,
}: ConsentRowProps) {
  return (
    <div className="px-4 pt-1">
        <div className="flex items-center justify-between gap-2 border border-amber-300 bg-amber-50 rounded-md p-2">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-xs font-semibold text-gray-900">
              Consent Pending
            </span>
            <span className="text-xs font-medium text-gray-500">
              Program reminders start once signed
            </span>
          </div>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={onSendConsent}
            className="!min-w-[unset] !shrink-0 !px-2 !py-1 !text-xs"
          >
            Resend
          </Button>
        </div>
      </div>
  );
}

type PCCProgramsProps = {
  programs: PCCProgramStatus[];
  isConsented: boolean;
  isEnrolling: boolean;
  patientId: number;
  patientEmail?: string | null;
  patientPhone?: string | null;
  selectedProgramTypes: PCCProgram[];
  hasEligiblePrograms: boolean;
  onToggle: (program: PCCProgramStatus, checked: boolean) => void;
  onEnrollSelected: () => Promise<void>;
};

function PCCPrograms({
  programs,
  isConsented,
  isEnrolling,
  patientId,
  patientEmail,
  patientPhone,
  selectedProgramTypes,
  hasEligiblePrograms,
  onToggle,
  onEnrollSelected,
}: PCCProgramsProps) {
  const [showSendConsentDialog, setShowSendConsentDialog] = useState(false);
  const actionablePrograms = programs.filter((program) => program.canEnroll);
  const nonActionablePrograms = programs
    .filter((program) => !program.canEnroll)
    .sort(compareNonActionablePrograms);
  const hasActionablePrograms = actionablePrograms.length > 0;
  const hasNonActionablePrograms = nonActionablePrograms.length > 0;
  const hasSelection = selectedProgramTypes.length > 0;
  const enrollTooltip = !hasEligiblePrograms
    ? 'No programs are eligible to enroll'
    : '';
  const emptySelectionTooltip = hasEligiblePrograms && !hasSelection
    ? 'Select at least one eligible program'
    : '';
  const tooltipTitle = notEmptyString(enrollTooltip)
    ? enrollTooltip
    : emptySelectionTooltip;
  const enrollButtonLabel = getEnrollButtonLabel(selectedProgramTypes, programs);

  return (
    <>
      {!isConsented ? (
        <ConsentRow
          onSendConsent={() => { setShowSendConsentDialog(true); }}
        />
      ) : null}
      <div className="flex flex-col">
        {hasNonActionablePrograms ? (
          <div className="mx-3.5 mb-4 flex flex-col gap-2 rounded-lg bg-[#F7F7F8] px-2.5 py-2">
            {nonActionablePrograms.map((program) => (
              <PCCPopoverStatusRow
                key={program.key}
                program={program}
              />
            ))}
          </div>
        ) : null}
        {hasActionablePrograms ? (
          <div
            className={clsx(
              'flex flex-col gap-1.5 px-3.5 pb-1.5 pt-4',
              {
                'border-t border-gray-200': hasNonActionablePrograms,
              },
            )}
          >
            <span className="pb-1 text-xs font-semibold text-gray-700">
              Ready to enroll
            </span>
            {actionablePrograms.map((program) => (
              <PCCProgramSelectionRow
                key={program.key}
                checkboxId={`pcc-program-${program.key}`}
                program={program}
                canEnroll={program.canEnroll}
                isEnrolling={isEnrolling}
                isSelected={selectedProgramTypes.includes(program.type)}
                onToggle={(checked) => { onToggle(program, checked); }}
              />
            ))}
          </div>
        ) : null}
        {hasActionablePrograms ? (
          <div className="px-[18px] pb-[18px] pt-3">
            <ToolTip
              title={tooltipTitle}
              hide={!notEmptyString(tooltipTitle)}
            >
              <span
                className={clsx(
                  'block w-full',
                  { 'cursor-not-allowed': notEmptyString(tooltipTitle) },
                )}
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  disabled={isEnrolling || notEmptyString(tooltipTitle)}
                  onClick={onEnrollSelected}
                  className="!w-full !max-w-none !min-w-[unset] !px-4 !py-2 !text-xs"
                >
                  {enrollButtonLabel}
                </Button>
              </span>
            </ToolTip>
          </div>
        ) : null}
      </div>
      {showSendConsentDialog ? (
        <SendConsentFormDialog
          patientId={patientId}
          patientEmail={patientEmail}
          patientPhone={patientPhone}
          onClose={() => { setShowSendConsentDialog(false); }}
        />
      ) : null}
    </>
  );
}

type Props = {
  isEnrolling: boolean;
  programs: PCCProgramStatus[];
  isConsented: boolean;
  patientId: number;
  patientEmail?: string | null;
  patientPhone?: string | null;
  onEnrollSelected: (programs: PCCProgram[]) => Promise<void>;
};

type ProgramStatusIconsProps = {
  programs: PCCProgramStatus[];
};

export function ProgramStatusIcons({
  programs,
}: ProgramStatusIconsProps) {
  return (
    <span className="flex items-center gap-2 border-l border-gray-300 pl-2">
      {programs.map((program, programIndex) => (
        <Fragment key={program.key}>
          {programIndex > 0 ? (
            <span aria-hidden="true" className="h-4 w-px bg-gray-300" />
          ) : null}
          <ProgramStatusMarker program={program} />
        </Fragment>
      ))}
    </span>
  );
}

export default function PCCPopoverContent({
  isEnrolling,
  programs,
  isConsented,
  patientId,
  patientEmail,
  patientPhone,
  onEnrollSelected,
}: Props) {
  const {
    selectedProgramTypes,
    hasEligiblePrograms,
    handleToggleProgram,
    handleEnrollSelected,
  } = usePCCPopoverScreen(
    programs,
    onEnrollSelected,
  );

  return (
    <div className="flex w-[420px] flex-col gap-2">
      <Header
        title="PCC programs"
        className="border-b border-gray-200 px-4 py-3"
      />
      <PCCPrograms
        programs={programs}
        isConsented={isConsented}
        isEnrolling={isEnrolling}
        patientId={patientId}
        patientEmail={patientEmail}
        patientPhone={patientPhone}
        selectedProgramTypes={selectedProgramTypes}
        hasEligiblePrograms={hasEligiblePrograms}
        onToggle={handleToggleProgram}
        onEnrollSelected={handleEnrollSelected}
      />
    </div>
  );
}
